import type {
  OpenWorldAgentConfiguration,
  OpenWorldBotId,
  OpenWorldConfiguration,
  OpenWorldConfigurationValidationIssue,
  OpenWorldConfigurationValidationResult,
  OpenWorldStartupPrompt,
  OpenWorldStorytellerConfiguration,
} from '@/lib/openWorlds/types'
import { OPEN_WORLD_DEFAULT_NOTES_PROMPT, OPEN_WORLD_DEFAULT_NOTES_SYSTEM_PROMPT } from '@/lib/openWorlds/notes'

type UnknownRecord = Record<string, unknown>

const issue = (
  issues: OpenWorldConfigurationValidationIssue[],
  path: string,
  message: string,
): void => {
  issues.push({ path, message })
}

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

function readObject(
  value: unknown,
  path: string,
  issues: OpenWorldConfigurationValidationIssue[],
): UnknownRecord | null {
  if (!isRecord(value)) {
    issue(issues, path, 'Must be an object.')
    return null
  }
  return value
}

function readString(
  value: unknown,
  path: string,
  issues: OpenWorldConfigurationValidationIssue[],
): string | null {
  if (typeof value !== 'string') {
    issue(issues, path, 'Must be a string.')
    return null
  }
  return value
}

function readSafeInteger(
  value: unknown,
  path: string,
  minimum: number,
  issues: OpenWorldConfigurationValidationIssue[],
): number | null {
  if (!Number.isSafeInteger(value) || (value as number) < minimum) {
    issue(issues, path, `Must be a safe integer greater than or equal to ${minimum}.`)
    return null
  }
  return value as number
}

function readBotId(
  value: unknown,
  path: string,
  issues: OpenWorldConfigurationValidationIssue[],
): OpenWorldBotId | undefined {
  if (value === null) return null

  if (typeof value === 'number') {
    if (Number.isSafeInteger(value) && value > 0) return value
    issue(issues, path, 'Must be null, a positive safe integer, or a nonblank string.')
    return undefined
  }

  if (typeof value === 'string') {
    if (value.trim()) return value
    issue(issues, path, 'Must be null, a positive safe integer, or a nonblank string.')
    return undefined
  }

  issue(issues, path, 'Must be null, a positive safe integer, or a nonblank string.')
  return undefined
}

function readStartupPrompt(
  value: unknown,
  path: string,
  issues: OpenWorldConfigurationValidationIssue[],
): OpenWorldStartupPrompt | null | undefined {
  if (value === null) return null

  const record = readObject(value, path, issues)
  if (!record) return undefined

  const text = readString(record.text, `${path}.text`, issues)
  const expiresAfterTurns = readSafeInteger(
    record.expiresAfterTurns,
    `${path}.expiresAfterTurns`,
    1,
    issues,
  )

  if (text !== null && !text.trim()) {
    issue(issues, `${path}.text`, 'Must contain nonblank text when the startup prompt is enabled.')
  }

  if (text === null || !text.trim() || expiresAfterTurns === null) return undefined
  return { text, expiresAfterTurns }
}

function readStoryteller(
  value: unknown,
  path: string,
  issues: OpenWorldConfigurationValidationIssue[],
): OpenWorldStorytellerConfiguration | null {
  const record = readObject(value, path, issues)
  if (!record) return null

  const botId = readBotId(record.botId, `${path}.botId`, issues)
  const staticPrompt = readString(record.staticPrompt, `${path}.staticPrompt`, issues)
  const startupPrompt = readStartupPrompt(record.startupPrompt, `${path}.startupPrompt`, issues)
  const initialMessage = readString(record.initialMessage, `${path}.initialMessage`, issues)
  const notesIntervalTurns = readSafeInteger(record.notesIntervalTurns === undefined ? 5 : record.notesIntervalTurns,
    `${path}.notesIntervalTurns`, 1, issues)
  const notesPrompt = readString(Object.hasOwn(record, 'notesPrompt') ? record.notesPrompt : OPEN_WORLD_DEFAULT_NOTES_PROMPT,
    `${path}.notesPrompt`, issues)

  const notesSystemPrompt = readString(record.notesSystemPrompt === undefined ? OPEN_WORLD_DEFAULT_NOTES_SYSTEM_PROMPT : record.notesSystemPrompt,
    `${path}.notesSystemPrompt`, issues)
  const notesHistory = record.notesHistory === undefined ? 'latest' : record.notesHistory
  if (!['none', 'latest', 'all'].includes(notesHistory as string)) issue(issues, `${path}.notesHistory`, 'Must be none, latest, or all.')
  const includeAllStoryNotes = record.includeAllStoryNotes === undefined ? false : record.includeAllStoryNotes
  if (typeof includeAllStoryNotes !== 'boolean') issue(issues, `${path}.includeAllStoryNotes`, 'Must be a boolean.')
  const notesIncludeStorytellerPrompt = record.notesIncludeStorytellerPrompt === undefined ? true : record.notesIncludeStorytellerPrompt
  if (typeof notesIncludeStorytellerPrompt !== 'boolean') issue(issues, `${path}.notesIncludeStorytellerPrompt`, 'Must be a boolean.')

  if (
    botId === undefined ||
    staticPrompt === null ||
    startupPrompt === undefined ||
    initialMessage === null || notesIntervalTurns === null || notesPrompt === null || notesSystemPrompt === null
  ) {
    return null
  }

  return { botId, staticPrompt, startupPrompt, initialMessage, notesIntervalTurns, notesPrompt, notesSystemPrompt,
    notesHistory: notesHistory as 'none' | 'latest' | 'all', includeAllStoryNotes: includeAllStoryNotes as boolean,
    notesIncludeStorytellerPrompt: notesIncludeStorytellerPrompt as boolean }
}

function readAgent(
  value: unknown,
  path: string,
  issues: OpenWorldConfigurationValidationIssue[],
): OpenWorldAgentConfiguration | null {
  const record = readObject(value, path, issues)
  if (!record) return null

  const botId = readBotId(record.botId, `${path}.botId`, issues)
  const instructions = readString(record.instructions, `${path}.instructions`, issues)
  const waitTurns = readSafeInteger(record.waitTurns, `${path}.waitTurns`, 0, issues)
  const minimumTurnInterval = readSafeInteger(
    record.minimumTurnInterval,
    `${path}.minimumTurnInterval`,
    1,
    issues,
  )
  const historyMessageLimit = readSafeInteger(
    record.historyMessageLimit,
    `${path}.historyMessageLimit`,
    0,
    issues,
  )

  if (
    botId === undefined ||
    instructions === null ||
    waitTurns === null ||
    minimumTurnInterval === null ||
    historyMessageLimit === null
  ) {
    return null
  }

  return { botId, instructions, waitTurns, minimumTurnInterval, historyMessageLimit }
}

export function createDefaultOpenWorldConfiguration(): OpenWorldConfiguration {
  return {
    schemaVersion: 1,
    useWorldSnapshot: true,
    storyteller: {
      botId: null,
      staticPrompt: '',
      startupPrompt: null,
      initialMessage: '',
      notesIntervalTurns: 5,
      notesPrompt: OPEN_WORLD_DEFAULT_NOTES_PROMPT,
      notesSystemPrompt: OPEN_WORLD_DEFAULT_NOTES_SYSTEM_PROMPT,
      notesHistory: 'latest',
      includeAllStoryNotes: false,
      notesIncludeStorytellerPrompt: true,
    },
    curator: {
      mode: 'standard',
      botId: null,
      instructions: '',
      waitTurns: 0,
      minimumTurnInterval: 1,
      historyMessageLimit: 12,
    },
    synthesis: {
      botId: null,
      instructions: '',
      waitTurns: 0,
      minimumTurnInterval: 3,
      historyMessageLimit: 12,
    },
    retrievalLifetimeTurns: 3,
  }
}

export function validateOpenWorldConfiguration(
  input: unknown,
): OpenWorldConfigurationValidationResult {
  const issues: OpenWorldConfigurationValidationIssue[] = []
  const record = readObject(input, 'configuration', issues)
  if (!record) return { success: false, issues }

  if (record.schemaVersion !== 1) {
    issue(issues, 'schemaVersion', 'Must be the supported schema version 1.')
  }
  if (record.useWorldSnapshot !== undefined && typeof record.useWorldSnapshot !== 'boolean') {
    issue(issues, 'useWorldSnapshot', 'Must be a boolean.')
  }

  const storyteller = readStoryteller(record.storyteller, 'storyteller', issues)
  const curator = readAgent(record.curator, 'curator', issues)
  const curatorRecord = isRecord(record.curator) ? record.curator : null
  const mode = curatorRecord?.mode ?? 'standard'
  if (curatorRecord && Object.hasOwn(curatorRecord, 'mode') &&
    !['standard', 'light'].includes(curatorRecord.mode as string)) {
    issue(issues, 'curator.mode', 'Must be standard or light.')
  }
  const synthesis = readAgent(record.synthesis, 'synthesis', issues)
  const retrievalLifetimeTurns = readSafeInteger(
    record.retrievalLifetimeTurns,
    'retrievalLifetimeTurns',
    1,
    issues,
  )

  if (curator && synthesis && synthesis.minimumTurnInterval < curator.minimumTurnInterval) {
    issue(
      issues,
      'synthesis.minimumTurnInterval',
      'Must be greater than or equal to curator.minimumTurnInterval.',
    )
  }

  if (issues.length || !storyteller || !curator || !synthesis || retrievalLifetimeTurns === null) {
    return { success: false, issues }
  }

  return {
    success: true,
    value: {
      schemaVersion: 1,
      useWorldSnapshot: record.useWorldSnapshot !== false,
      storyteller,
      curator: { ...curator, mode: mode as 'standard' | 'light' },
      synthesis,
      retrievalLifetimeTurns,
    },
  }
}
